import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { Link } from "react-router-dom";
import { getRequest } from "../../utils/httpRequest";
import { errorNotification } from "../../utils/helpers";
import NewsletterInline from "../Newsletter/NewsletterInline";
import BlogSubscribe from "./BlogSubscribe";
import { BlogPostResource } from "./types";
import ShareLinks from "./ShareLinks";
import BlogComments from "./Comments";

interface Props {
  match: {
    params: {
      slug: string;
    };
  };
  // From DynamicLayoutRoute.
  currentUser?: { kind?: string };
  handleShowSignInSideBar?: (redirectUrl?: string) => void;
}

interface State {
  loading: boolean;
  post: BlogPostResource;
}

class BlogPostShow extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      post: null
    };
  }

  componentDidMount() {
    this.fetchPost();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.match.params.slug !== this.props.match.params.slug) {
      this.fetchPost();
    }
  }

  fetchPost = () => {
    this.setState({ loading: true, post: null });

    return getRequest(`/api/blog-posts/${this.props.match.params.slug}.json`)
      .then((response: AxiosResponse) => {
        document.title = `${response.data.seo_title} | 42courses`;
        this.setState({ post: response.data, loading: false });
      })
      .catch(error => {
        this.setState({ loading: false });
        errorNotification(
          "Error loading blog post",
          JSON.stringify(error.response?.data || error.message)
        );
      });
  };

  relatedPostsHeading = (post: BlogPostResource) => {
    if (post.skill_area) {
      return `More on ${post.skill_area.name}`;
    }

    if (post.thought_leader) {
      return `More from ${post.thought_leader.name}`;
    }

    return "Related posts";
  };

  render() {
    const { loading, post } = this.state;

    if (loading) {
      return (
        <main className="blog-post-page">
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        </main>
      );
    }

    if (!post) {
      return (
        <main className="blog-post-page">
          <section className="blog-post-page__empty">
            <h2>Post not found</h2>
            <p>We could not load this post.</p>
          </section>
        </main>
      );
    }

    return (
      <article className="blog-post-page">
        <header className="blog-post-page__hero">
          <span className="blog-post-page__watermark" aria-hidden="true">42</span>
          <div className="blog-post-page__hero-copy">
            <div className="blog-post-page__meta">
              <Link to="/blog" className="blog-post-page__back-link">
                &larr; Latest posts
              </Link>
              <span className="blog-post-page__date">
                {new Date(post.published_at).toDateString()}
              </span>
            </div>
            <h1>{post.title}</h1>
            {post.excerpt && <p>{post.excerpt}</p>}
          </div>

          {post.hero_image_url && (
            <div className="blog-post-page__hero-image">
              <img src={post.hero_image_url} alt="" />
            </div>
          )}
        </header>

        <main className="blog-post-page__main">
          <div className="blog-post-page__article-card">
            <div
              className="blog-post-page__body"
              dangerouslySetInnerHTML={{ __html: post.formatted_body }}
            />

            <BlogSubscribe variant="box" />

            <ShareLinks post={post} />

            {(post.skill_area || post.thought_leader || post.author) && (
              <aside className="blog-post-page__links" aria-label="Related pages">
                {/* The byline goes first: who wrote it matters more to a reader
                    who just finished it than which course it relates to. */}
                {post.author && (
                  <Link to={post.author.path}>
                    More from {post.author.name}
                  </Link>
                )}
                {post.skill_area && (
                  <Link to={post.skill_area.path}>
                    More {post.skill_area.name} courses and articles
                  </Link>
                )}
                {post.thought_leader && (
                  <Link to={post.thought_leader.path}>
                    More from {post.thought_leader.name}
                  </Link>
                )}
              </aside>
            )}
          </div>

          <BlogComments
            slug={post.slug}
            currentUserKind={this.props.currentUser?.kind}
            handleShowSignInSideBar={this.props.handleShowSignInSideBar}
          />

          <NewsletterInline />

          {post.related_posts?.length > 0 && (
            <section className="blog-post-page__related" aria-label="Related posts">
              <h2>{this.relatedPostsHeading(post)}</h2>
              <div className="blog-post-page__related-grid">
                {post.related_posts.map(relatedPost => (
                  <article className="blog-post-page__related-card" key={relatedPost.id}>
                    <Link to={relatedPost.blog_post_path}>
                      {relatedPost.hero_image_url && (
                        <div className="blog-post-page__related-thumb">
                          <img src={relatedPost.hero_image_url} alt="" loading="lazy" />
                        </div>
                      )}
                      <div className="blog-post-page__related-body">
                        {relatedPost.skill_area && <em>{relatedPost.skill_area.name}</em>}
                        <span>{new Date(relatedPost.published_at).toDateString()}</span>
                        <h3>{relatedPost.title}</h3>
                        {relatedPost.excerpt && <p>{relatedPost.excerpt}</p>}
                        <strong>Read post</strong>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </section>
          )}
        </main>
      </article>
    );
  }
}

export default BlogPostShow;
